import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getUserStorage, clearAllStorage } from "util/local_storage";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import checkPermission from "constants/permission";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import SyncLockIcon from "@mui/icons-material/SyncLock";
import Menu from "@mui/material/Menu";
import MenuItemMui from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import { getStockPathByUserType, getUserType } from "../util/auth";

interface HeaderProps {
  showMenu?: boolean;
}

interface MenuItem {
  label: string;
  icon?: string;
  roleCondition?: string[];
  onClick?: () => void;
  subItems?: MenuItem[];
  path?: string;
  guard?: boolean; // if true, the item is not displayed.
}

/**
 * TabletHeader component
 * Renders a settings icon that opens a dropdown menu for password change and logout actions.
 * Designed for tablet/mobile view (visible on xs screens, hidden on md and up).
 * @param {Object} props
 * @param {() => void} props.changePassword - Callback to trigger password change flow
 * @param {() => void} props.logout - Callback to trigger logout flow
 */
function TabletHeader({
  changePassword,
  logout,
}: {
  changePassword: () => void;
  logout: () => void;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ display: { xs: "block", md: "none" } }}
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <SettingsIcon sx={{ width: 28, height: 28 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItemMui onClick={changePassword}>
          <ListItemIcon>
            <SyncLockIcon fontSize="medium" />
          </ListItemIcon>
          パスワード変更
        </MenuItemMui>
        <MenuItemMui onClick={logout}>
          <ListItemIcon>
            <LogoutIcon fontSize="medium" />
          </ListItemIcon>
          ログアウト
        </MenuItemMui>
      </Menu>
    </>
  );
}

/**
 * Header component
 * @param {object} props - Component props
 * @param {boolean} [props.showMenu] - Flag to show or hide the menu
 */
function Header({ showMenu = true }: HeaderProps) {
  const navigate = useNavigate();
  const user = getUserStorage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>([]); // Track which submenus are open
  const [stockPath, setStockPath] = useState("");
  const location = useLocation();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuToggle = (label: string) => {
    setOpenMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label],
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false); // Close the drawer when navigating
  };

  const handleLogout = () => {
    clearAllStorage();
    navigate("/login");
  };

  const handleChangePassword = () => {
    navigate("/forgot-password");
  };

  const masterList: MenuItem[] = [
    {
      label: "見積管理",
      icon: "/img/features/menu/paper.svg",
      subItems: [
        { label: "見積一覧", path: "/quote-list" },
        { label: "新規見積", path: "/new-quote" },
      ],
      roleCondition: ["C0001", "C0002", "C0003"],
    },
    {
      label: getUserType() === "C0004" ? "在庫情報一覧" : "在庫管理",
      icon: "/img/features/menu/inventory.svg",
      path: stockPath,
      guard: getUserType() === "C0006" && user?.identificationName !== "YH_COMPANY",
    },
    {
      label: getUserType() === "C0004" ? "受注管理" : "発注管理",
      icon: "/img/features/menu/order.svg",
      // ToDo: 店舗・企業・得意先は後々リリースする。
      roleCondition: ["C0001", "C0002", "C0003", "C0004", "C0006", "C0007", "C0008"],
      // roleCondition: ["C0001","C0004", "C0007", "C0008"],
      subItems: [
        { label: "注文依頼", path: "/orders?mode=ask", roleCondition: ["C0001"] },
        { label: "注文履歴", path: "/orders?mode=history", roleCondition: ["C0001"] },
        { label: "注文確認", path: "/order/form?mode=create&check=true", roleCondition: ["C0001"] },
        { label: "注文作成", path: "/order/form?mode=create", roleCondition: ["C0001"] },
        { label: "注文履歴", path: "/orders/store", roleCondition: ["C0002", "C0003", "C0006"] },
        // ToDo: 店舗・企業・得意先は後々リリースする。
        // { label: "注文確認", path: "/order/store/form?mode=create&check=true", roleCondition: ["C0002", "C0003", "C0006"] },
        // { label: "注文作成", path: "/order/store/form?mode=create", roleCondition: ["C0002", "C0003", "C0006"] },
        { label: "受注一覧", path: "/orders/supplier", roleCondition: ["C0004", "C0007", "C0008"] },
      ],
      guard: getUserType() === "C0006" && user?.identificationName !== "YH_COMPANY",
    },
    {
      label: "倉庫管理",
      icon: "/img/features/menu/inventory.svg",
      roleCondition: ["C0004"],
      path: "/supplier-warehouse",
    },
    // {
    //   label: "出荷管理",
    //   icon: "/img/features/menu/shipping.svg",
    //   path: "/shipping-management",
    //   roleCondition: ["C0001", "C0002", "C0003"],
    // },
    {
      label: "マスタ管理",
      icon: "/img/features/menu/master.svg",
      roleCondition: ["C0001"],
      subItems: [
        { label: "商品管理", path: "/mst-item" },
        { label: "タイヤ管理", path: "/mst-store/tire-list" },
        { label: "ホイール管理", path: "/mst-wheel" },
        { label: "商品オプション", path: "/mst-option" },
        { label: "CSVアップロード", path: "/mst-csv" },
        { label: "分類管理", path: "/mst-category" },
        { label: "店舗管理", path: "/mst-store" },
        { label: "企業管理", path: "/mst-yh-company" },
        { label: "得意先管理", path: "/mst-client" },
        { label: "仕入先管理", path: "/mst-vendor" },
        { label: "利用者管理", path: "/mst-user" },
        { label: "工賃管理", path: "/mst-proc-fee/setting" },
        { label: "タイヤ売価設定", path: "/item-price/setting" },
        { label: "ホイール売価設定", path: "/mst-wheel-price/setting" },
        { label: "汎用マスタメンテナンス", path: "/mst-maintenance" },
      ],
    },
    {
      label: "マスタ設定",
      icon: "/img/features/menu/master.svg",
      roleCondition: ["C0002"],
      subItems: [
        { label: "工賃設定", path: "/mst-proc-fee/setting" },
        { label: "4本特価タイヤ設定", path: "/mst-4bundle-price-twset/setting" },
        { label: "タイヤ売価設定", path: "/item-price/setting" },
        { label: "ホイール売価設定", path: "/mst-wheel-price/setting" },
      ],
    },
    {
      label: "更新情報",
      icon: "/img/features/menu/update.svg",
      path: "/updates",
      roleCondition: ["C0001", "C0002", "C0003"],
    },
    {
      label: "利用レポート",
      icon: "/img/features/menu/report.svg",
      roleCondition: ["C0001", "C0002", "C0003", "C0006"],
      subItems: [
        { label: "タイヤ・ホイール", path: "/tire-report" },
        { label: "システム利用状況", path: "/system-usage" },
        { label: "SMS送信件数", path: "/sms-report", roleCondition: ["C0001"] },
        { label: "店舗BO連携エラー", path: "/bo-error-report", roleCondition: ["C0001"] },
      ],
    },
    {
      label: "閉じる",
      icon: "/img/features/menu/bell.svg",
      onClick: () => setDrawerOpen(false),
    },
  ];

  useEffect(() => {
    setStockPath(getStockPathByUserType() ?? "");
  }, [location.pathname]);

  const getUserName = () => {
    const userType = getUserType();
    const idName =
      (getUserType() === "C0006"
        ? user?.identificationName === "YH_COMPANY"
          ? "YH企業"
          : user?.identificationName === "CLIENT_COMPANY"
            ? "得意先企業"
            : ""
        : user?.identificationName) ?? "";
    if (userType && ["C0002", "C0003", "C0006"].includes(userType)) {
      return `${user?.userName ?? ""} (${user?.storeName ?? ""})`;
    }
    return `${user?.userName ?? ""} (${idName})`;
  };

  return (
    <header
      style={{
        backgroundColor: "var(--primary-500)",
        padding: "0 20px",
        display: "flex",
        justifyContent: "space-between",
        height: "50px",
        alignItems: "center",
      }}
    >
      {showMenu && (
        <div>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            <span>YHBB MENU</span>
          </Link>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            sx={{
              "& .MuiDrawer-paper": {
                top: "50px",
              },
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
            ModalProps={{
              BackdropProps: {
                sx: { backgroundColor: "transparent" },
              },
            }}
          >
            <div style={{ width: 280, padding: "10px" }}>
              <List>
                {masterList
                  .filter((item) => !item.guard)
                  .filter((item) =>
                    item.roleCondition ? item.roleCondition.includes(getUserType() ?? "") : true,
                  )
                  .map((item) => (
                    <div key={item.label}>
                      {item && checkPermission(item.path || "") && (
                        <ListItem
                          component="div"
                          onClick={() => {
                            if (item.subItems) {
                              handleMenuToggle(item.label);
                            } else if (item.onClick) {
                              item.onClick();
                            } else if (item.path) {
                              handleNavigate(item.path);
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            padding: "10px",
                            borderRadius: "4px",
                            "&:hover": { backgroundColor: "var(--offwhite-150)" },
                            gap: "10px",
                          }}
                        >
                          <img src={item.icon} alt={item.label} />
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: "20px" }}
                          />
                          {item.subItems && <ArrowDropDownIcon />}
                        </ListItem>
                      )}
                      {item.subItems && openMenus.includes(item.label) && (
                        <List component="div" disablePadding>
                          {item.subItems
                            .filter(
                              (subItem) =>
                                checkPermission(subItem.path || "") &&
                                (!subItem.roleCondition ||
                                  subItem.roleCondition.includes(getUserType() ?? "")),
                            )
                            .map((subItem) => (
                              <ListItem
                                key={subItem.label}
                                button
                                onClick={() => handleNavigate(subItem.path || "")}
                                sx={{ paddingLeft: "60px" }}
                              >
                                <ListItemText
                                  primary={subItem.label}
                                  primaryTypographyProps={{ fontSize: "20px" }}
                                />
                              </ListItem>
                            ))}
                        </List>
                      )}
                    </div>
                  ))}
              </List>
            </div>
          </Drawer>
        </div>
      )}

      <Stack direction="row" alignItems="center" spacing={2}>
        {showMenu && <span>{getUserName()}でログイン中</span>}
        {showMenu && (
          <Typography
            component="span"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              display: { xs: "none", md: "inline" },
            }}
            onClick={handleChangePassword}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleChangePassword();
            }}
            tabIndex={0}
            role="button"
          >
            パスワード変更
          </Typography>
        )}
        {showMenu && (
          <Typography
            component="span"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              display: { xs: "none", md: "inline" },
            }}
            onClick={handleLogout}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogout();
            }}
            tabIndex={0}
            role="button"
          >
            ログアウト
          </Typography>
        )}
        {showMenu && <TabletHeader changePassword={handleChangePassword} logout={handleLogout} />}
      </Stack>
    </header>
  );
}

export default Header;
